import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import CheckIcon from "@heroicons/react/20/solid/CheckIcon";
import ChevronLeftIcon from "@heroicons/react/20/solid/ChevronLeftIcon";
import { Link, useSearchParams } from "react-router-dom";

import useYardActionEvents from "@hsl/core/hooks/useYardAnalyticsEvents";
import cx from "@hsl/core/utils/cx";
import useFCSubscriptionStore from "@hsl/fc-subscription/store";
import type { StepName } from "@hsl/fc-subscription/types";

import useTranslation, { Translate } from "../../../hooks/useTranslation";
import { ApiError } from "../../../openapi";
import Button from "../../Button";
import Loading from "../../Loading";
import Spinner from "../../Spinner";
import { useBannerText } from "../Root";
import { useHideTabBar } from "../useHideTabBar";
import { FlowStep } from "./FlowStep";
import AddDetails from "./Steps/AddDetails";
import AddRecipient from "./Steps/AddRecipient";
import SelectDocuments from "./Steps/SelectDocuments";
import SelectFunds from "./Steps/SelectFunds";
import { VerifyEmail } from "./Steps/VerifyEmail";
import { useBackToFC } from "./useBackToFC";

export type FlowType = "share" | "subscribe" | "watch";
interface Props<T extends StepName[]> {
    steps: T;
    defaultStep?: T[keyof T];
    finalStepMode?: "hide" | "submit";
    type: FlowType;
}

export const FlowStepper = <T extends StepName[]>({
    steps,
    defaultStep,
    finalStepMode = "hide",
    type,
}: Props<T>) => {
    useBannerText();
    useHideTabBar();
    const { buttonClickEvent } = useYardActionEvents();
    const [error, setError] = useState<string>();
    const [searchParams, setSearchParams] = useSearchParams();
    const ref = useRef<{ submit: () => Promise<void> }>(null);
    const [loading, setLoading] = useState(false);
    const loadingUser = useFCSubscriptionStore((state) => state.loadingUser);

    const tx = useTranslation();

    const step = searchParams.get("step") as StepName | null;
    const thisStep = step ?? defaultStep;
    const getStepData = useFCSubscriptionStore(
        (state) => state.getFlowStepData,
    );
    const thisStepIdx = thisStep ? steps.findIndex((s) => s === thisStep) : -1;
    const backHref = useBackToFC();
    const clearBasket = useFCSubscriptionStore((store) => store.clearBasket);

    const isFinalStep = thisStepIdx === steps.length - 1;

    const showNavButtons = isFinalStep
        ? finalStepMode === "submit"
        : step !== "verify-email";

    const scrollToTop = () => {
        console.log("scrollToTop");
        window.scrollTo({
            top: 0,
            behavior: "smooth", // for a smooth scrolling
        });
    };

    const goTo = async (dir: "prev" | "next") => {
        const delta = dir === "next" ? 1 : -1;
        const nextStep = steps[thisStepIdx + delta]!;
        const { result: genericMsg } = tx("There was an error");
        if (ref.current?.submit && dir === "next") {
            setError("");
            setLoading(true);
            buttonClickEvent({
                name: step as string,
                details: isFinalStep ? "Submit" : "Next",
            });
            try {
                await ref.current.submit();
                if (nextStep) {
                    setSearchParams((params) => {
                        params.set("step", nextStep);
                        return params;
                    });
                }
            } catch (e) {
                const stepData = thisStep
                    ? getStepData(thisStep as StepName)
                    : null;

                const { result: incompleteMsg } = stepData
                    ? tx(stepData.incompleteMsg ?? "")
                    : { result: "" };
                if (e instanceof ApiError) {
                    console.error("Error from FCS API: ", e.body);
                    setError(
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
                        e.body?.message && e.body.message !== "Unknown error"
                            ? // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                              e.body.message
                            : genericMsg,
                    );
                } else if (e instanceof Error) {
                    setError(
                        e.message ? e.message : incompleteMsg ?? genericMsg,
                    );
                } else {
                    setError(genericMsg);
                }
            } finally {
                setLoading(false);
            }
        } else {
            const data = thisStep ? getStepData(thisStep as StepName) : null;
            const canGoNext = dir === "prev" || data?.complete;
            if (canGoNext) {
                setSearchParams((params) => {
                    params.set("step", nextStep);
                    return params;
                });
                setError("");
            } else {
                setError(data?.incompleteMsg ?? genericMsg);
            }
            buttonClickEvent({
                name: dir === "prev" ? "Previous" : "Next",
            });
        }
        scrollToTop();
    };

    useEffect(() => {
        if (!step || !steps.includes(step)) {
            setSearchParams((params) => {
                params.set("step", (defaultStep ?? steps[0]) as string);
                return params;
            });
        }
    }, [step, steps, defaultStep, setSearchParams]);

    if (steps.length !== new Set(steps).size)
        throw new Error(tx("Duplicate steps in flow").result);

    return (
        <div className="mx-auto flex w-full max-w-[898px] flex-col px-4">
            <Link
                to={backHref}
                className="text-taupe-grey my-3 inline-flex flex-row items-center py-2 pr-2"
            >
                <ChevronLeftIcon className="mr-1.5 inline-block h-5 w-5" />
                <Translate>Back to fund centre</Translate>
            </Link>
            {loadingUser || !step ? (
                <Loading />
            ) : (
                <div>
                    <StepGuide step={step} steps={steps} />
                    <FlowStep
                        key={step}
                        step={step}
                        steps={steps}
                        error={error}
                    >
                        {({ step }) => {
                            switch (step) {
                                case "select-funds":
                                    return <SelectFunds flowType={type} />;
                                case "select-documents":
                                    return (
                                        <SelectDocuments
                                            controlRef={ref}
                                            flowType={type}
                                        />
                                    );
                                case "add-details":
                                    return <AddDetails controlRef={ref} />;
                                case "add-recipient":
                                    return <AddRecipient controlRef={ref} />;
                                case "verify-email":
                                    return <VerifyEmail />;
                            }
                        }}
                    </FlowStep>
                    {showNavButtons && (
                        <div className="mt-8 flex items-center justify-between gap-2">
                            <Link
                                to={backHref}
                                className="text-core-blue text-sm underline sm:text-base"
                                onClick={() => {
                                    clearBasket();
                                    buttonClickEvent({
                                        name: "Cancel",
                                    });
                                }}
                            >
                                <Translate>Cancel</Translate>
                            </Link>
                            <div className="flex items-center gap-4">
                                {steps.indexOf(step) > 0 && (
                                    <Button
                                        color="white"
                                        variant="large"
                                        onClick={() => goTo("prev")}
                                        disabled={loading}
                                        className="text-sm sm:text-base"
                                    >
                                        <Translate>Previous</Translate>
                                    </Button>
                                )}
                                <Button
                                    color="primary"
                                    variant="large"
                                    form={step}
                                    className="flex-row-reverse text-sm sm:text-base"
                                    onClick={() => goTo("next")}
                                    disabled={loading}
                                    icon={
                                        loading ? (
                                            <Spinner className="my-0 ml-4 mr-0 h-4 w-4 animate-spin" />
                                        ) : undefined
                                    }
                                >
                                    <Translate>
                                        {isFinalStep ? "Submit" : "Next"}
                                    </Translate>
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export const useStepName = () => {
    const translate = useTranslation();

    return useCallback(
        (step: StepName) => {
            switch (step) {
                case "select-funds":
                    return translate("Select funds").result;
                case "select-documents":
                    return translate("Select documents").result;
                case "add-details":
                    return translate("Add your details").result;
                case "verify-email":
                    return translate("Verify email").result;
                case "add-recipient":
                    return translate("Add recipient").result;
                default:
                    throw new Error("Unknown step");
            }
        },
        [translate],
    );
};

export const StepGuide = ({
    steps,
    step,
}: {
    step: StepName;
    steps: StepName[];
}) => {
    const getStepName = useStepName();
    return (
        <div className="flex w-full flex-row items-center justify-center gap-4 pb-6 pt-3">
            {steps.map((s) => {
                const active = step === s;
                const prev = steps.indexOf(step) > steps.indexOf(s);
                return (
                    <Fragment key={`step-name:${s}`}>
                        {steps.indexOf(s) > 0 && (
                            <div className="border-platinum max-w-[40px] flex-1 shrink border-t" />
                        )}
                        <div className="flex flex-row items-center gap-2 max-sm:flex-col max-sm:text-[10px]">
                            <div
                                className={cx(
                                    "bg-platinum text-battleship-grey grid h-5 w-5 shrink-0 place-items-center rounded-full transition-colors sm:h-8 sm:w-8",
                                    active && "bg-core-blue text-white",
                                    prev && "bg-core-green text-white",
                                )}
                            >
                                {prev ? (
                                    <CheckIcon className="h-4 w-4 sm:h-6 sm:w-6" />
                                ) : (
                                    <span className="max-sm:text-xs sm:text-lg">
                                        {steps.indexOf(s) + 1}
                                    </span>
                                )}
                            </div>
                            <p className="min-w-fit max-sm:text-center">
                                {getStepName(s)}
                            </p>
                        </div>
                    </Fragment>
                );
            })}
        </div>
    );
};
